import { Pressable, Text, View } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';

import { colors } from '../../colors';
import { styles } from './ErrorState.styles';

export enum ErrorStateVariant {
  Danger = 'danger',
  Error = 'error',
  Info = 'info'
}

export type ErrorStateCloseConfig = {
  onPress: () => void;
  /** Accessible label for the close button. Required so the host owns the localized string. */
  accessibilityLabel: string;
};

export type ErrorStateProps = {
  title: string;
  message: string;
  testID?: string;
  /**
   * `Danger` is the amber warning banner, `Error` the red failure banner, and `Info` a neutral
   * banner for non-error states such as a server-assigned spot.
   */
  variant?: ErrorStateVariant;
  /**
   * Close button config. Pass it to render the close button, omit it to hide. Dismissal is the
   * host's to own: this only reports the tap through {@link ErrorStateCloseConfig.onPress} and
   * never hides itself.
   */
  close?: ErrorStateCloseConfig;
};

function DangerIcon({ testID }: { testID: string }) {
  return (
    <View style={styles.iconContainer} testID={testID}>
      <Svg width={22} height={19} viewBox="0 0 22 19" fill="none">
        <Path
          d="M0 19H22L11 0L0 19ZM3.47 17L11 3.99L18.53 17H3.47ZM10 14H12V16H10V14ZM10 8H12V12H10V8Z"
          fill={colors.errorBannerBorder}
        />
      </Svg>
    </View>
  );
}

function ErrorIcon({ testID }: { testID: string }) {
  return (
    <View style={styles.iconContainer} testID={testID}>
      <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
        <Path
          d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
          fill={colors.errorBannerErrorIcon}
        />
        <Path
          d="M14.8284 7.75738L16.2426 9.1716L13.4142 12L16.2426 14.8285L14.8284 16.2427L12 13.4142L9.17157 16.2427L7.75736 14.8285L10.5858 12L7.75736 9.1716L9.17157 7.75738L12 10.5858L14.8284 7.75738Z"
          fill={colors.errorBannerErrorIcon}
        />
      </Svg>
    </View>
  );
}

function InfoIcon({ testID }: { testID: string }) {
  return (
    <View style={styles.iconContainer} testID={testID}>
      <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
        <Circle cx={10} cy={10} r={9} stroke={colors.infoBannerBorder} strokeWidth={1.5} />
        <Path
          d="M10 9V14M10 6H10.01"
          stroke={colors.infoBannerBorder}
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </Svg>
    </View>
  );
}

function CloseIcon() {
  return (
    <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
      <Path
        d="M11 1L1 11M1 1L11 11"
        stroke={colors.errorBannerText}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    </Svg>
  );
}

const containerStyleByVariant: Record<ErrorStateVariant, (typeof styles)['containerDanger']> = {
  [ErrorStateVariant.Danger]: styles.containerDanger,
  [ErrorStateVariant.Error]: styles.containerError,
  [ErrorStateVariant.Info]: styles.containerInfo
};

/**
 * Reusable banner for layout/spot-booking messages. Title and message are configurable.
 * Matches Figma: Layout unavailable — node 2496:39621.
 */
export default function ErrorState({
  title,
  message,
  testID = 'error-state',
  variant = ErrorStateVariant.Danger,
  close
}: ErrorStateProps) {
  const isInfo = variant === ErrorStateVariant.Info;

  return (
    <View style={[styles.container, containerStyleByVariant[variant]]} testID={testID}>
      {variant === ErrorStateVariant.Error ? (
        <ErrorIcon testID={`${testID}-icon-error`} />
      ) : isInfo ? (
        <InfoIcon testID={`${testID}-icon-info`} />
      ) : (
        <DangerIcon testID={`${testID}-icon-danger`} />
      )}
      <View style={[styles.textColumn, close ? styles.textColumnClosable : null]}>
        <Text style={isInfo ? styles.infoTitle : styles.title} testID={`${testID}-title`}>
          {title}
        </Text>
        <Text style={isInfo ? styles.infoMessage : styles.message} testID={`${testID}-message`}>
          {message}
        </Text>
      </View>
      {close ? (
        <Pressable
          onPress={close.onPress}
          accessibilityRole="button"
          accessibilityLabel={close.accessibilityLabel}
          hitSlop={8}
          style={styles.closeButton}
          testID={`${testID}-close`}
        >
          <CloseIcon />
        </Pressable>
      ) : null}
    </View>
  );
}
